/* ============================================
   ZOKUE CUES - Main JavaScript
   ============================================ */

// ===== <quantity-input> Custom Element =====
// Implements the Refresh theme's <quantity-input> web component so that
// cart + / - buttons actually update cart line quantities via /cart/change.js.
// MUST be defined at top level (before DOMContentLoaded) so that the browser
// upgrades any <quantity-input> tags as soon as it sees them in the HTML.
if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
        constructor() {
            super();
            this.input = this.querySelector('.quantity__input');
            this.changeEvent = new Event('change', { bubbles: true });
        }

        connectedCallback() {
            this.querySelectorAll('button').forEach((button) => {
                button.addEventListener('click', this.onButtonClick.bind(this));
            });
            this.input.addEventListener('change', this.onInputChange.bind(this));
        }

        onButtonClick(event) {
            event.preventDefault();
            const previousValue = this.input.value;

            if (event.target.name === 'plus') {
                this.input.value = Math.max(parseInt(this.input.min) || 0, parseInt(previousValue) + 1);
            } else if (event.target.name === 'minus') {
                this.input.value = Math.max(parseInt(this.input.min) || 0, parseInt(previousValue) - 1);
            }

            if (this.input.value !== previousValue) {
                this.input.dispatchEvent(this.changeEvent);
            }
        }

        onInputChange(event) {
            this.updateQuantity(event.target.value);
        }

        updateQuantity(quantity) {
            // Resolve cart line key from the input, the wrapper, or the closest cart-item
            const cartItem = this.closest('.cart-item, [data-cart-item]');
            const key = this.input.dataset.key
                || (cartItem && (cartItem.dataset.key || cartItem.dataset.cartItemKey));

            if (!key) {
                // Fallback: just submit the form so the page handles it
                if (this.input.form) {
                    this.input.form.submit();
                }
                return;
            }

            // Disable buttons during request so user can't double-tap
            this.querySelectorAll('button').forEach((b) => (b.disabled = true));

            fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: key, quantity: parseInt(quantity) || 0 }),
            })
            .then((response) => response.json())
            .then((cart) => {
                // Refresh the page so Refresh's cart template re-renders the new
                // totals, line item subtotals, and any free-shipping bars correctly.
                location.reload();
            })
            .catch((err) => {
                console.error('[ZOKUE] cart update failed:', err);
                this.querySelectorAll('button').forEach((b) => (b.disabled = false));
            });
        }
    }

    customElements.define('quantity-input', QuantityInput);
}

document.addEventListener('DOMContentLoaded', function() {

    // ===== Hero Suit Navigation — Smooth Scroll to Sections =====
    // Use native anchor behavior as fallback; enhance with smooth scroll.
    document.querySelectorAll('.hero__suit[href^="#"]').forEach(function(link) {
        link.addEventListener('click', function(e) {
            var href = this.getAttribute('href');
            if (!href || href === '#') return;
            var target = document.querySelector(href);
            if (!target) {
                // Target not found — let browser handle natively
                console.warn('[ZOKUE] target not found:', href, '— letting browser handle');
                return;
            }
            e.preventDefault();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            if (history.replaceState) {
                history.replaceState(null, '', href);
            }
        });
    });

    // ===== Product Card Click Navigation (Robust Fallback) =====
    // Ensures product cards navigate to the product page no matter
    // whether the snippet wraps the card in <a> or <div>.
    document.querySelectorAll('.product-card').forEach(function(card) {
        // Resolve the product URL from the card itself or its descendants
        var productUrl = card.getAttribute('data-product-url')
            || card.getAttribute('href');

        if (!productUrl) {
            var innerLink = card.querySelector('a[href*="/products/"]');
            if (innerLink) productUrl = innerLink.getAttribute('href');
        }

        if (!productUrl) return;

        // If the card is already an <a>, browser navigation handles it.
        // Still ensure the cursor is set.
        card.style.cursor = 'pointer';

        if (card.tagName !== 'A') {
            // <div> card — attach manual navigation as a safety net
            card.addEventListener('click', function(e) {
                // Don't intercept clicks on real <a> or <button> inside the card
                if (e.target.closest('a, button')) return;
                window.location.href = productUrl;
            });
        }
    });

    // ===== Mobile Menu Toggle =====
    const navToggle = document.querySelector('.nav__toggle');
    const navMenu = document.querySelector('.nav__menu');

    if (navToggle) {
        navToggle.addEventListener('click', () => {
            navMenu.classList.toggle('active');
        });
    }

    // ===== Carousel System =====
    document.querySelectorAll('[data-carousel]').forEach(carousel => {
        const track = carousel.querySelector('.carousel__track');

        // Find the buttons by climbing up to the nearest section ancestor
        // This is more reliable than carousel.parentElement.querySelector
        let sectionRoot = carousel.closest('.section, .zokue-collection-products, [class*="section"]');
        if (!sectionRoot) sectionRoot = carousel.parentElement;

        const prevBtn = sectionRoot.querySelector('[data-prev]');
        const nextBtn = sectionRoot.querySelector('[data-next]');

        if (!track) return;

        const scrollAmount = () => {
            const firstCard = track.querySelector('.product-card');
            if (!firstCard) return 320;
            const gap = parseInt(getComputedStyle(track).gap) || 20;
            return firstCard.offsetWidth + gap;
        };

        const scrollPrev = (e) => {
            if (e) { e.preventDefault(); e.stopPropagation(); }
            track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
        };
        const scrollNext = (e) => {
            if (e) { e.preventDefault(); e.stopPropagation(); }
            track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        };

        if (prevBtn) {
            prevBtn.addEventListener('click', scrollPrev);
            // Ensure button isn't covered by anything
            prevBtn.style.pointerEvents = 'auto';
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', scrollNext);
            nextBtn.style.pointerEvents = 'auto';
        }

        // Touch/drag support
        let isDown = false;
        let startX;
        let scrollLeft;

        track.addEventListener('mousedown', (e) => {
            isDown = true;
            track.style.cursor = 'grabbing';
            startX = e.pageX - track.offsetLeft;
            scrollLeft = track.scrollLeft;
        });

        track.addEventListener('mouseleave', () => {
            isDown = false;
            track.style.cursor = '';
        });

        track.addEventListener('mouseup', () => {
            isDown = false;
            track.style.cursor = '';
        });

        track.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - track.offsetLeft;
            const walk = (x - startX) * 1.5;
            track.scrollLeft = scrollLeft - walk;
        });
    });

    // ===== Scroll Reveal Animation =====
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-up');
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);

    document.querySelectorAll('.section, .features, .banner-cta, .newsletter').forEach(el => {
        observer.observe(el);
    });

    // ===== Smooth Scroll for Nav Links (skip .hero__suit, handled above) =====
    document.querySelectorAll('a[href^="#"]').forEach(link => {
        // Skip hero suit links — they have their own handler
        if (link.classList.contains('hero__suit')) return;
        link.addEventListener('click', (e) => {
            const href = link.getAttribute('href');
            if (href === '#') return;
            const target = document.querySelector(href);
            if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: 'smooth' });
                if (navMenu) navMenu.classList.remove('active');
            }
        });
    });

    // ===== Nav Background on Scroll =====
    const nav = document.querySelector('.nav');
    let lastScroll = 0;

    window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;
        if (currentScroll > 50) {
            nav.style.background = 'rgba(13, 13, 13, 0.98)';
            nav.style.boxShadow = '0 2px 20px rgba(0,0,0,0.3)';
        } else {
            nav.style.background = 'rgba(13, 13, 13, 0.95)';
            nav.style.boxShadow = 'none';
        }
        lastScroll = currentScroll;
    });

    // ===== Add to Cart Feedback (DISABLED — cards are now full links) =====
// The old behavior changed "View Product" button text to "Added!"
// This is no longer needed since the entire card is now a clickable link
// and would interfere with product page navigation.

    // ===== ZOKUE Cart Drawer =====
    initCartDrawer();
});

// ===== Cart Drawer =====
function initCartDrawer() {
    var drawer = document.getElementById('zokueCartDrawer');
    if (!drawer) return;

    var body = drawer.querySelector('[data-cart-drawer-body]');
    var footer = drawer.querySelector('[data-cart-drawer-footer]');
    var subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    var countBadge = document.querySelector('[data-cart-count-badge]');
    var progressBar = drawer.querySelector('[data-cart-progress-bar]');
    var progressText = drawer.querySelector('[data-cart-progress-text]');

    // Free shipping bar config: read from Theme settings (data-fsbar), fallback to defaults
    var fsbar = drawer.querySelector('[data-fsbar]');
    var FS_THRESHOLD = 20000; // cents
    var FS_SHOW_AMOUNT = true;
    var FS_TEXT_REMAINING = 'Add [amount] more for free shipping';
    var FS_TEXT_REACHED = '🎉 You qualify for free shipping!';
    if (fsbar) {
        var _t = parseInt(fsbar.getAttribute('data-fsbar-threshold'), 10);
        if (!isNaN(_t) && _t > 0) FS_THRESHOLD = _t;
        FS_SHOW_AMOUNT = fsbar.getAttribute('data-fsbar-show-amount') !== 'false';
        var _tr = fsbar.getAttribute('data-fsbar-text-remaining');
        if (_tr) FS_TEXT_REMAINING = _tr;
        var _tc = fsbar.getAttribute('data-fsbar-text-reached');
        if (_tc) FS_TEXT_REACHED = _tc;
    }
    var MONEY_FORMAT = (function() {
        try { return (window.Shopify && window.Shopify.money_format) || '${{ amount }}'; } catch (e) { return '${{ amount }}'; }
    })();

    function formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            try { return Shopify.formatMoney(cents, MONEY_FORMAT); } catch (e) {}
        }
        return '$' + (cents / 100).toFixed(2);
    }

    function open() {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('zokue-drawer-open');
        // Focus management for accessibility
        setTimeout(function() {
            var close = drawer.querySelector('.zokue-cart-drawer__close');
            if (close) close.focus();
        }, 200);
    }

    function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('zokue-drawer-open');
    }

    function renderItem(item) {
        var linePrice = (item.final_line_price != null) ? item.final_line_price : (item.line_price);
        var originalLinePrice = (item.original_line_price != null) ? item.original_line_price : linePrice;
        var hasCompare = originalLinePrice > linePrice;
        var imgUrl = item.image ? (item.image.replace(/(\.[^.]*)$/, '_120x$1')) : '';
        var variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
        var properties = '';
        if (item.properties && Object.keys(item.properties).length) {
            Object.keys(item.properties).forEach(function(k) {
                if (k === '_unused' || !item.properties[k]) return;
                properties += '<div class="zokue-cart-drawer__item-prop"><span>' + escapeHtml(k) + ':</span> ' + escapeHtml(item.properties[k]) + '</div>';
            });
        }
        return (
            '<div class="zokue-cart-drawer__item" data-cart-line="' + item.key + '">' +
                '<a class="zokue-cart-drawer__item-img" href="' + item.url + '">' +
                    (imgUrl ? '<img src="' + imgUrl + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '<span>♠</span>') +
                '</a>' +
                '<div class="zokue-cart-drawer__item-info">' +
                    '<a class="zokue-cart-drawer__item-title" href="' + item.url + '">' + escapeHtml(item.title) + '</a>' +
                    (variantTitle ? '<div class="zokue-cart-drawer__item-variant">' + escapeHtml(variantTitle) + '</div>' : '') +
                    properties +
                    '<div class="zokue-cart-drawer__item-price">' +
                        '<span class="zokue-cart-drawer__item-price-current">' + formatMoney(linePrice) + '</span>' +
                        (hasCompare ? '<span class="zokue-cart-drawer__item-price-old">' + formatMoney(originalLinePrice) + '</span>' : '') +
                    '</div>' +
                    '<div class="zokue-cart-drawer__item-qty">' +
                        '<button type="button" data-cart-qty="minus" data-key="' + item.key + '" aria-label="Decrease">−</button>' +
                        '<input type="number" value="' + item.quantity + '" min="0" data-cart-qty-input data-key="' + item.key + '" aria-label="Quantity">' +
                        '<button type="button" data-cart-qty="plus" data-key="' + item.key + '" aria-label="Increase">+</button>' +
                    '</div>' +
                '</div>' +
                '<button type="button" class="zokue-cart-drawer__item-remove" data-cart-remove="' + item.key + '" aria-label="Remove">' +
                    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"/></svg>' +
                '</button>' +
            '</div>'
        );
    }

    function render(cart) {
        // Update count badge in header
        if (countBadge) {
            countBadge.textContent = cart.item_count;
            countBadge.classList.toggle('is-empty', cart.item_count === 0);
            if (cart.item_count > 0) {
                countBadge.classList.add('is-pulsing');
                setTimeout(function() { countBadge.classList.remove('is-pulsing'); }, 600);
            }
        }

        // Update count chip in drawer title
        var drawerCount = drawer ? drawer.querySelector('[data-cart-drawer-count]') : null;
        if (drawerCount) {
            drawerCount.textContent = cart.item_count;
            drawerCount.classList.toggle('is-zero', cart.item_count === 0);
        }

        if (!cart.items || cart.items.length === 0) {
            body.innerHTML =
                '<p class="zokue-cart-drawer__empty">' +
                    '<span class="zokue-cart-drawer__empty-icon">♠ ♦ ♣ ♥</span>' +
                    '<span>Your cart is empty</span>' +
                    '<a href="/collections/all" class="zokue-cart-drawer__continue">Continue shopping</a>' +
                '</p>';
            if (footer) footer.hidden = true;
        } else {
            body.innerHTML = cart.items.map(renderItem).join('');
            if (footer) footer.hidden = false;
        }

        if (subtotalEl) {
            subtotalEl.textContent = formatMoney(cart.total_price || cart.cart_total_price || 0);
        }

        // Free shipping progress
        if (progressBar && progressText) {
            var total = cart.total_price || 0;
            var pct = Math.min(100, Math.round((total / FS_THRESHOLD) * 100));
            progressBar.style.width = pct + '%';
            if (total >= FS_THRESHOLD) {
                progressText.innerHTML = FS_TEXT_REACHED.replace(/\[amount\]/g, '').replace(/\s{2,}/g, ' ').trim();
                progressBar.classList.add('is-complete');
            } else {
                var remaining = FS_THRESHOLD - total;
                var amountHtml = FS_SHOW_AMOUNT ? '<strong>' + formatMoney(remaining) + '</strong>' : '';
                progressText.innerHTML = FS_TEXT_REMAINING.replace(/\[amount\]/g, amountHtml).replace(/\s{2,}/g, ' ').trim();
                progressBar.classList.remove('is-complete');
            }
        }
    }

    function refresh() {
        return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function(r) { return r.json(); })
            .then(render)
            .catch(function(err) { console.error('[ZOKUE] cart refresh failed:', err); });
    }

    function changeQty(key, qty) {
        qty = Math.max(0, parseInt(qty, 10) || 0);
        var lineEl = body.querySelector('[data-cart-line="' + key + '"]');
        if (lineEl) lineEl.classList.add('is-updating');
        return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: qty })
        })
        .then(function(r) { return r.json(); })
        .then(render)
        .catch(function(err) { console.error('[ZOKUE] cart change failed:', err); });
    }

    function removeItem(key) {
        return changeQty(key, 0);
    }

    function escapeHtml(s) {
        if (s == null) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // Bind open/close
    document.querySelectorAll('[data-cart-drawer-open]').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
            e.preventDefault();
            refresh().then(open);
        });
    });
    document.querySelectorAll('[data-cart-drawer-close]').forEach(function(el) {
        el.addEventListener('click', function(e) {
            e.preventDefault();
            close();
        });
    });

    // Quantity buttons in drawer
    body.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-cart-qty]');
        if (!btn) return;
        var key = btn.dataset.key;
        var input = body.querySelector('[data-cart-qty-input][data-key="' + key + '"]');
        if (!input) return;
        var current = parseInt(input.value, 10) || 0;
        var next = current + (btn.dataset.cartQty === 'plus' ? 1 : -1);
        if (next < 0) next = 0;
        input.value = next;
        changeQty(key, next);
    });

    body.addEventListener('change', function(e) {
        var input = e.target.closest('[data-cart-qty-input]');
        if (!input) return;
        changeQty(input.dataset.key, input.value);
    });

    body.addEventListener('click', function(e) {
        var rm = e.target.closest('[data-cart-remove]');
        if (!rm) return;
        removeItem(rm.dataset.cartRemove);
    });

    // ESC to close
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Listen for global "refresh cart" events
    document.addEventListener('zokue:cart:refresh', function() { refresh(); });

    // Initial render so count badge is accurate on load
    refresh();

    // Public API
    window.ZokueCart = { open: open, close: close, refresh: refresh, render: render };
}
